import { useEffect, useMemo, useState } from 'react';
import { inventoryStore } from '@/stores/inventory.store';
import { modalStore } from '@/stores/modal.store';
import { ApiRequest } from '@/utils/axios';
import { notify } from '@/utils/notify';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import CoinflipInventoryPicker from '@/components/Modals/Inventory/CoinflipInventoryPicker';
import CoinflipJoinFooter from '@/components/Modals/Inventory/CoinflipJoinFooter';

interface Props {
    minbet: number;
}

const JackpotSteamDeposit = ({ minbet }: Props) => {
    const {
        loadSteamInventory,
        uncacheSteamInventory,
        steamItems,
        clearInventory,
        maxSelectedItems,
        setSelectedById,
        selectedItemIds,
    } = inventoryStore((state) => state);
    const { setTitle, setComponent, setData, close } = modalStore((state) => state);

    const [loading, setLoading] = useState(true);

    async function load(force = false) {
        setLoading(true);
        try {
            const {
                data: {
                    response: {
                        global: { appid },
                    },
                },
            } = await ApiRequest.get('/settings/list');
            if (force) await uncacheSteamInventory(appid, 2);
            await loadSteamInventory(appid, 2);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load().then();
        return () => clearInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pickerItems = useMemo(
        () =>
            steamItems.map((it) => ({
                id: BigInt(it.assetId),
                name: it.name,
                priceUsd: it.priceUsd,
                colorHex: it.colorHex,
                icon: it.icon,
            })),
        [steamItems]
    );

    const inventoryTotal = pickerItems.reduce((s, i) => s + i.priceUsd, 0);
    const selected = pickerItems.filter((i) => selectedItemIds.has(i.id));
    const selectedAmount = selected.reduce((s, i) => s + i.priceUsd, 0);
    const canBet = selectedAmount >= minbet;

    async function jackpotClosed(): Promise<boolean> {
        const {
            data: { response },
        } = await ApiRequest.get('/jackpot');
        return response.state.finishedAt != null;
    }

    async function bet() {
        if (!canBet) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title={'Error'} text={`Min bet is $${minbet}`} />
            );
            return;
        }
        setTitle('Loading...');
        setComponent('Loader');

        if (await jackpotClosed()) {
            close();
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title={'Error'} text={`Jackpot is in finish state, try later`} />
            );
            return;
        }

        const assetIds = Array.from(selectedItemIds).map((i) => i.toString());
        const res: {
            data: { success: boolean; response: { offerId: number } };
        } = await ApiRequest.post('/jackpot/join/steam', { assetIds });

        if (res?.data?.success && res?.data?.response.offerId) {
            setComponent('Steam trade');
            setTitle('CONFIRM TRADE');
            setData({ tradeId: res.data.response.offerId });
        } else {
            close();
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title={'Error'} text={'Something went wrong'} />
            );
        }
    }

    return (
        <CoinflipInventoryPicker
            items={pickerItems}
            selectedItemIds={selectedItemIds}
            setSelectedById={setSelectedById}
            maxItems={maxSelectedItems}
            loading={loading}
            onRefresh={() => load(true)}
            footer={
                <CoinflipJoinFooter
                    inventoryTotal={inventoryTotal}
                    selectedCount={selected.length}
                    selectedAmount={selectedAmount}
                    onAction={bet}
                    actionLabel="MAKE DEPOSIT"
                    actionDisabled={!canBet}
                />
            }
        />
    );
};

export default JackpotSteamDeposit;
